import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye, Ruler, MapPin, UserCheck, Fingerprint, Lock, Ear, Phone, Mail, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Reveal, Stagger, item, SectionHeading } from "@/components/Reveal";
import { PageHero, CtaBanner } from "@/components/Sections";
import { FounderAvatar } from "@/components/FounderAvatar";
import { CONTACT } from "@/components/Footer";

const VALUE_ICONS = [Heart, Eye, Ruler, MapPin];
const WHY_ICONS = [UserCheck, Fingerprint, Lock, Ear];

export default function AboutPage() {
  const { t, to } = useLang();
  const a = t.about;
  return (
    <div data-testid="about-page">
      <PageHero eyebrow={a.hero.eyebrow} title={a.hero.title} sub={a.hero.sub} id="about" />

      <section className="container-x py-20 lg:py-28" data-testid="about-journey-section">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><FounderAvatar /></Reveal>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow={a.journey.eyebrow} title={a.journey.title} id="about-journey" />
            <Reveal delay={0.1} className="mt-6 space-y-4">
              {a.journey.p.map((p, i) => <p key={i} className="text-base leading-relaxed text-slate-600 md:text-lg">{p}</p>)}
            </Reveal>
            <Stagger className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4" stagger={0.08}>
              {a.stats.map((s) => (
                <motion.div key={s.label} variants={item} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`about-stat-${a.stats.indexOf(s) + 1}`}>
                  <p className="font-display text-2xl font-extrabold text-teal-700">{s.value}</p>
                  <p className="mt-1 text-xs text-slate-500">{s.label}</p>
                </motion.div>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="about-values-section">
        <div className="container-x">
          <SectionHeading eyebrow={a.values.eyebrow} title={a.values.title} id="about-values" />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {a.values.items.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <motion.div key={v.title} variants={item} className="card-soft" data-testid={`about-value-${i + 1}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700"><Icon size={22} /></span>
                  <h3 className="mt-6 font-display text-lg font-bold text-slate-900 sm:text-xl">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.desc}</p>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-navy py-20 text-white lg:py-28" data-testid="about-why-section">
        <div className="container-x relative grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHeading eyebrow={a.why.eyebrow} title={a.why.title} light id="about-why" /></div>
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:col-span-7" stagger={0.08}>
            {a.why.items.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <motion.div key={w.title} variants={item} className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur" data-testid={`about-why-${i + 1}`}>
                  <Icon size={22} className="text-teal-300" />
                  <h3 className="mt-4 font-display text-lg font-bold">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{w.desc}</p>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="container-x py-20 lg:py-28" data-testid="about-contact-section">
        <Reveal>
          <div className="grid gap-8 rounded-[2.5rem] border border-slate-200 bg-white p-10 lg:grid-cols-12 lg:p-14">
            <div className="lg:col-span-5">
              <p className="eyebrow">{a.contact.title}</p>
              <h2 className="mt-4 font-display text-3xl font-bold text-slate-900">SIDLY</h2>
              <p className="mt-2 text-sm text-slate-500">{t.footer.company}</p>
            </div>
            <ul className="space-y-4 text-slate-700 lg:col-span-4">
              <li className="flex items-center gap-3"><Phone size={18} className="text-teal-600" /><a href={CONTACT.phoneHref} className="hover:text-teal-700" data-testid="about-phone-link">{CONTACT.phone}</a></li>
              <li className="flex items-center gap-3"><Mail size={18} className="text-teal-600" /><a href={`mailto:${CONTACT.email}`} className="hover:text-teal-700" data-testid="about-email-link">{CONTACT.email}</a></li>
              <li className="flex items-start gap-3"><MapPin size={18} className="mt-0.5 text-teal-600" /><span>{CONTACT.address}</span></li>
            </ul>
            <div className="lg:col-span-3 lg:text-right"><Link to={to("contact")} className="btn-primary" data-testid="about-contact-cta">{a.contact.cta} <ArrowRight size={16} /></Link></div>
          </div>
        </Reveal>
      </section>

      <CtaBanner title={t.home.cta.title} sub={t.home.cta.sub} btn1={t.home.cta.btn1} btn2={t.home.cta.btn2} to1={to("contact")} to2={`${to("contact")}#contact-forms`} id="about-cta" />
    </div>
  );
}
